import { Paginated } from '../../../core/models/paginated.model';

/** Most SKUs the Best sellers rail can hold; the add buttons turn off once it is full. */
export const MAX_BEST_SELLERS = 10;

/** Item counts for the catalogue section tabs (B24, B25, B29, B30). */
export interface CatalogCounts {
  skus: number;
  bestSellers: number;
  brands: number;
  categories: number;
  banners: number;
  /** ISO date-time of the last SAP item sync. */
  lastSync: string;
}

export interface SkuPromotion {
  code: string;
  name: string;
  nameAr: string;
  kind: 'percent' | 'amount';
  value: number;
  /** ISO date. */
  until: string;
}

/** A row of the products list (B24), from `ProductResource`. */
export interface SkuSummary {
  id: string;
  /** SAP item code. */
  code: string;
  /** Display name (English); falls back to the SAP description. */
  name: string;
  /** Display name (Arabic); empty when missing. */
  nameAr: string;
  /** Empty when there is no photo yet (the app shows the Cashcool logo). */
  imageUrl: string;
  brandId: string;
  /** Brand and category names come in the request language: `brandAr`/`categoryAr` repeat them. */
  brand: string;
  brandAr: string;
  /** Not in the API yet: empty, the brand shows in the default colour. */
  brandColor: string;
  categoryId: string;
  category: string;
  categoryAr: string;
  /** SAP retail price of one sales unit; null when SAP has none. */
  listPrice: number | null;
  /** Main-warehouse stock (what the apps use), in the sales unit. */
  stock: number;
  /** SAP sales unit, e.g. "Carton", in the request language; empty when SAP has none (shown as ctn). */
  unit: string;
  /** "In app" switch. */
  visible: boolean;
  /** Really shown in the apps: valid in SAP, visible, active category, stock rule. */
  liveInApp: boolean;
  /** Position in the hand-picked best sellers (B25), from 1; 0 when not on the list. */
  bestSellerRank: number;
}

export type SkuFilter = '' | 'missingImage' | 'hidden';

export interface ProductsQuery {
  page: number;
  pageSize: number;
  search: string;
  brand: string;
  category: string;
  filter: SkuFilter;
}

export interface CatalogOption {
  id: string;
  label: string;
  labelAr: string;
}

/** Brands and categories that have products, for the list filters (`GET /products/options`). */
export interface ProductsOptions {
  brands: CatalogOption[];
  categories: CatalogOption[];
}

/** KPI cards and tab badges of B24. `null`: not in the API yet ("—"). */
export interface ProductsKpis {
  total: number;
  live: number | null;
  hidden: number;
  missingImage: number;
  missingArabic: number | null;
  newThisWeek: number | null;
  /** SKUs on the best-seller list (B25). */
  bestSellers: number;
}

/** `GET /products`. */
export interface ProductsPage extends Paginated<SkuSummary> {
  kpis: ProductsKpis;
}

export interface WarehouseStock {
  code: string;
  name: string;
  nameAr: string;
  /** `main`: the main warehouse (`is_main`), even when SAP also lists it as a vehicle. */
  kind: 'main' | 'warehouse' | 'van';
  /** Sales rep, for vans. */
  salesRep?: string;
  salesRepAr?: string;
  cartons: number;
}

export interface SkuImage {
  url: string;
  fileName: string;
  width: number;
  height: number;
  sizeKb: number;
}

/** Sales figures of B26. Not in the API yet: all `null` ("—"). */
export interface SkuStats {
  cartons30d: number | null;
  orders: number | null;
  outlets: number | null;
  avgCartons: number | null;
}

/** `GET /products/{id}` (B26, B27), from `ProductDetailResource`. */
export interface SkuDetail extends SkuSummary {
  /** SAP item description, read only. */
  sapDescription: string;
  image: SkuImage | null;
  descriptionEn: string;
  descriptionAr: string;
  keywords: string[];
  /** Pieces in one sales unit, e.g. 24 for a carton of 24 cans; null when SAP has none. */
  packSize: number | null;
  supplier: string;
  supplierAr: string;
  sapCategoryCode: string;
  sapCategory: string;
  /** Price after the running promotion. Promotions are not in the API yet: equals `listPrice`. */
  appPrice: number | null;
  /** Not in the API yet: empty. */
  promotions: SkuPromotion[];
  warehouses: WarehouseStock[];
  hideWhenOutOfStock: boolean;
  bestSeller: boolean;
  /** Products in the Best sellers rail. */
  bestSellersCount: number;
  stats: SkuStats;
  /** Last refresh from SAP; empty when never synced. */
  lastSync: string;
  /** Last back-office save; empty when never saved. */
  savedAt: string;
  savedBy: string;
  savedByAr: string;
}

/**
 * B27 Save: `PUT /products/{id}`, then the photo (`POST`/`DELETE /products/{id}/photo`) and the best-seller
 * switch (`POST`/`DELETE /products/{id}/best-seller`) when they changed.
 */
export interface UpdateSkuBody {
  name: string;
  nameAr: string;
  descriptionEn: string;
  descriptionAr: string;
  keywords: string[];
  /** New photo to upload; `null` keeps the saved one unless `removePhoto`. */
  photo: File | null;
  removePhoto: boolean;
  hideWhenOutOfStock: boolean;
  bestSeller: boolean;
}

/** A SKU on the best-seller list (B25). */
export interface BestSeller {
  skuId: string;
  code: string;
  name: string;
  nameAr: string;
  brand: string;
  brandAr: string;
  imageUrl: string;
}

/**
 * B25, from `GET /products?filter=best_sellers`. The API has no draft: a saved order is live at once, so
 * `published` is always true. `live` (rail switch) and `savedAt` are not in the API yet.
 */
export interface BestSellers {
  items: BestSeller[];
  live: boolean;
  /** Empty: not in the API yet. */
  savedAt: string;
  published: boolean;
}

export interface Brand {
  id: string;
  name: string;
  nameAr: string;
  supplier: string;
  supplierAr: string;
  skus: number;
  color: string;
  /** Empty when missing. */
  logoUrl: string;
  /** Position in the Home brands grid, from 1; 0 when not on Home. */
  homeOrder: number;
  hasOffer: boolean;
  /** Words shops search with; the brand and its SKUs come up for them. */
  keywords: string[];
}

export type BrandFilter = '' | 'missingLogo' | 'noKeywords';

export interface UpdateBrandBody {
  name: string;
  nameAr: string;
  logoUrl: string;
  /** Brand colour in the app, `#RRGGBB`. */
  color: string;
  homeOrder: number;
  keywords: string[];
}

export interface Category {
  id: string;
  sapCode: string;
  name: string;
  nameAr: string;
  skus: number;
  imageUrl: string;
  /** Tile background in the app. */
  tint: string;
  visible: boolean;
  /** Words shops search with; the category comes up for them. */
  keywords: string[];
}

export interface UpdateCategoryBody {
  name: string;
  nameAr: string;
  imageUrl: string;
  tint: string;
  visible: boolean;
  keywords: string[];
}

/** `POST /uploads`: where an uploaded image now lives. */
export interface UploadResult {
  url: string;
  fileName: string;
  width: number;
  height: number;
  sizeKb: number;
}
